import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { CompactWorkshopCard } from "@/components/CompactWorkshopCard";
import { useMyWorkshopStatus } from "@/lib/myWorkshopStatus";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, MapPin, UserRound, Award, CalendarCheck, Cake } from "lucide-react";
import { formatK } from "@/lib/metrics";
import { MaestroWhatsApp } from "@/components/AskMaestroActions";
import { FavoriteMaestroButton } from "@/components/FavoriteMaestroButton";
import { useAuth } from "@/context/AuthContext";
import { ReportMaestroButton } from "@/components/ReportMaestro";
import { RatingSummary, RateMaestroBox } from "@/components/MaestroRating";

function StatCard({ icon: Icon, value, label, testId }) {
  return (
    <Card className="p-4 sm:p-5 flex items-center gap-3" data-testid={testId}>
      <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
      <div className="min-w-0">
        <p className="text-2xl font-extrabold leading-none tabular-nums" data-testid={`${testId}-value`}>{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </Card>
  );
}

export default function MaestroProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const mine = useMyWorkshopStatus();
  useEffect(() => {
    setLoading(true);
    api.get(`/maestros/${id}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-10"><Skeleton className="h-40 rounded-2xl" /></div>;
  if (!data) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-muted-foreground" data-testid="maestro-not-found">Maestro no encontrado.</div>;
  const { profile, activos, realizados } = data;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="maestro-profile">
      <Link to="/explorar" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Volver a explorar
      </Link>

      <Card className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-28 h-28 rounded-2xl overflow-hidden bg-muted grid place-items-center shrink-0">
          {profile.avatar
            ? <img src={fileUrl(profile.avatar)} alt={profile.name} className="w-full h-full object-cover" data-testid="maestro-avatar" />
            : <UserRound className="w-12 h-12 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight min-w-0 break-words" data-testid="maestro-name">{profile.name}</h1>
            <MaestroWhatsApp endpoint={`/maestros/${id}/whatsapp`} tipRight
              text={`Hola ${profile.name}, vi tu perfil en VIVOTaller y tengo una consulta.`} />
          </div>
          {profile.profession && <p className="text-primary font-medium mt-1" data-testid="maestro-profession">{profile.profession}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-4"><FavoriteMaestroButton businessId={profile.id} name={profile.name} />{user?.owned_business_id !== profile.id && <ReportMaestroButton businessId={profile.id} maestroName={profile.name} />}</div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
            {profile.years_experience != null && (
              <span className="flex items-center gap-1.5" data-testid="maestro-years"><Award className="w-4 h-4" />{profile.years_experience} años de trayectoria</span>
            )}
            {profile.age != null && (
              <span className="flex items-center gap-1.5" data-testid="maestro-age"><Cake className="w-4 h-4" />{profile.age} años</span>
            )}
            {profile.commune && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[profile.commune, profile.region].filter(Boolean).join(", ")}</span>}
          </div>
          {profile.categories?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">{profile.categories.map((c) => <Badge key={c} variant="outline">{c}</Badge>)}</div>
          )}
        </div>
      </Card>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="maestro-stats">
        <StatCard icon={CalendarCheck} value={formatK(profile.stats?.sessions_done)} label="Talleres realizados" testId="stat-sessions" />
        <StatCard icon={UserRound} value={formatK(profile.stats?.students_total)} label="Alumnos totales" testId="stat-students" />
        <div className="col-span-2 lg:col-span-1 space-y-3"><RatingSummary rep={profile.stats?.reputation} />{user?.role === "customer" && <RateMaestroBox businessId={profile.id} onRated={() => api.get(`/maestros/${id}`).then((r) => setData(r.data))} />}</div>
      </div>

      {profile.bio && (
        <section className="mt-8" data-testid="maestro-bio">
          <h2 className="text-lg font-bold mb-2">Sobre el Maestro</h2>
          <p className="text-muted-foreground whitespace-pre-line leading-relaxed">{profile.bio}</p>
        </section>
      )}

      {profile.gallery?.length > 0 && (
        <section className="mt-8" data-testid="maestro-gallery">
          <h2 className="text-lg font-bold mb-3">Su espacio de trabajo</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {profile.gallery.map((src, i) => (
              <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                <img src={fileUrl(src)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10" data-testid="maestro-activos">
        <h2 className="text-lg font-bold mb-4">Talleres disponibles</h2>
        {activos.length === 0 ? (
          <p className="text-muted-foreground text-sm">Este Maestro no tiene talleres con cupos abiertos por ahora.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {activos.map((w) => <CompactWorkshopCard key={w.id} workshop={w} paid={mine.paid.has(w.id)} saved={mine.saved.has(w.id)} />)}
          </div>
        )}
      </section>

      {realizados.length > 0 && (
        <section className="mt-10" data-testid="maestro-realizados">
          <h2 className="text-lg font-bold mb-4">Talleres realizados anteriormente</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 opacity-90">
            {realizados.map((w) => <CompactWorkshopCard key={w.id} workshop={w} />)}
          </div>
        </section>
      )}
    </div>
  );
}
